import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";

// One image serves stage and prod, so the key is baked into both;
// only prod hostnames may track.
function isProdHost(): boolean {
  const h = location.hostname;
  return (
    /(^|\.)condense\.chat$/.test(h) && !/(^|\.)stage\.condense\.chat$/.test(h)
  );
}

const key = isProdHost()
  ? (import.meta.env.VITE_POSTHOG_KEY as string | undefined)
  : undefined;

const CONSENT_COOKIE = "cc_cookie_preferences";
const LEGACY_CONSENT_COOKIE = "cc_analytics_consent";
const CONSENT_VERSION = "v2";
type Consent = "granted" | "denied";
type ConsentPreferences = {
  analytics: boolean;
  replay: boolean;
};
type CapturedRequest = { name?: string };

const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

function maskSensitiveText(text: string): string {
  return text.replace(EMAIL_RE, "[email redacted]");
}

function redactCapturedUrl<T extends CapturedRequest>(request: T): T {
  if (request.name) request.name = request.name.split(/[?#]/, 1)[0];
  return request;
}

function consentDomain(): string | undefined {
  const h = location.hostname;
  if (h === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(h)) return undefined;
  const parts = h.split(".");
  if (parts.length <= 2) return undefined;
  return "." + parts.slice(-2).join(".");
}

function getLegacyConsent(): Consent | null {
  const m = document.cookie.match(
    /(?:^|;\s*)cc_analytics_consent=(granted|denied)/,
  );
  return m ? (m[1] as Consent) : null;
}

function getCurrentPreferences(): ConsentPreferences | null {
  const m = document.cookie.match(
    /(?:^|;\s*)cc_cookie_preferences=v2-a([01])-r([01])/,
  );
  if (!m) return null;
  const analytics = m[1] === "1";
  return { analytics, replay: analytics && m[2] === "1" };
}

export function getConsent(): ConsentPreferences | null {
  const current = getCurrentPreferences();
  if (current) return current;
  const legacy = getLegacyConsent();
  if (legacy === "granted") return { analytics: true, replay: false };
  if (legacy === "denied") return { analytics: false, replay: false };
  return null;
}

export function analyticsConsentHeader(): Record<string, string> {
  return getConsent()?.analytics
    ? { "x-condense-analytics-consent": "granted" }
    : {};
}

function setConsent(preferences: ConsentPreferences): void {
  const domain = consentDomain();
  const secure = location.protocol === "https:" ? "; Secure" : "";
  const value = `${CONSENT_VERSION}-a${preferences.analytics ? 1 : 0}-r${
    preferences.analytics && preferences.replay ? 1 : 0
  }`;
  document.cookie =
    `${CONSENT_COOKIE}=${value}; path=/; max-age=${60 * 60 * 24 * 180}` +
    `; SameSite=Lax${domain ? `; domain=${domain}` : ""}${secure}`;

  const expired =
    `${LEGACY_CONSENT_COOKIE}=; path=/; max-age=0; SameSite=Lax` + secure;
  document.cookie = expired;
  if (domain) document.cookie = `${expired}; domain=${domain}`;
}

let started = false;

export function startPostHog(replay = false): void {
  if (!key || started) return;
  started = true;
  posthog.init(key, {
    // Same-origin proxy (see src/condense/ph_proxy.py) so events survive
    // content-blockers and a script-src 'self' CSP. Named /relay, not the
    // well-known /ingest, which blocklists now match. ui_host keeps
    // toolbar / "view in PostHog" links pointing at the real EU app.
    api_host: "/relay",
    ui_host: "https://eu.posthog.com",
    // Cookie persistence + cross_subdomain_cookie so the anonymous landing
    // visit (set on *.condense.chat) merges into the person we identify here.
    persistence: "cookie",
    cross_subdomain_cookie: true,
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: true,
    // Keep replays useful without recording credentials or customer PII.
    // `ph-no-capture` excludes one-time API secrets at the component level.
    disable_session_recording: !replay,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskTextFn: maskSensitiveText,
      // The one-time secret component already has this stable class. Blocking
      // it here protects replay without changing token UI or token behavior.
      blockSelector: ".secret-reveal",
      maskCapturedNetworkRequestFn: redactCapturedUrl,
    },
    disable_surveys: true,
    opt_out_persistence_by_default: true,
  });
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false });
  }
}

function applyConsent(preferences: ConsentPreferences): void {
  if (!preferences.analytics) {
    if (started) {
      posthog.stopSessionRecording();
      posthog.opt_out_capturing();
    }
    return;
  }
  if (!started) {
    startPostHog(preferences.replay);
  } else {
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing({ captureEventName: false });
    }
    if (preferences.replay) posthog.startSessionRecording();
    else posthog.stopSessionRecording();
  }
}

// Call init on load only if the visitor already consented (e.g. accepted on
// the landing site — the cookie is shared across *.condense.chat).
const storedConsent = getConsent();
if (key && storedConsent?.analytics) startPostHog(storedConsent.replay);

type ClerkUser = ReturnType<typeof useUser>["user"];

function identifyUser(user: NonNullable<ClerkUser>): void {
  if (!started) return;
  const properties = {
    email: user.primaryEmailAddress?.emailAddress,
    name: user.fullName ?? undefined,
    clerk_user_id: user.id,
  };
  if (posthog.get_distinct_id() === user.id) {
    // Keep mutable person properties current without emitting another
    // $identify event on each page load.
    posthog.setPersonProperties(properties);
  } else {
    // Merges the anonymous cookie history into this account. The $identify
    // event carries the conversion time and arrives through the IP-preserving
    // first-party relay.
    posthog.identify(user.id, properties);
  }
}

// Stitches the anonymous visit to the signed-in user the moment Clerk
// reports a session. No-op until PostHog has been started (post-consent).
export function PostHogUser() {
  const { isSignedIn, isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLoaded || !started) return;
    if (isSignedIn && user) {
      identifyUser(user);
    } else if (posthog.get_property("$user_id")) {
      // Prevent a second person on a shared browser from inheriting the first
      // person's identified cookie and subsequent activity.
      posthog.reset();
    }
  }, [isSignedIn, isLoaded, user]);
  return null;
}

export function ConsentBanner() {
  const { isSignedIn, isLoaded, user } = useUser();
  const [visible, setVisible] = useState(
    () =>
      Boolean(key) &&
      getCurrentPreferences() === null &&
      getLegacyConsent() !== "denied",
  );
  const [shown, setShown] = useState(false);
  const initial = getConsent();
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [replay, setReplay] = useState(initial?.replay ?? false);

  useEffect(() => {
    if (visible) requestAnimationFrame(() => setShown(true));
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    setShown(false);
    setTimeout(() => setVisible(false), 240);
  };
  const save = (preferences: ConsentPreferences) => {
    setConsent(preferences);
    applyConsent(preferences);
    if (preferences.analytics && isLoaded && isSignedIn && user)
      identifyUser(user);
    dismiss();
  };

  return (
    <div
      className={`cc-banner${shown ? " cc-show" : ""}`}
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
    >
      <div className="cc-heading">Yes, cookies. We know.</div>
      <p className="cc-copy">
        Optional analytics help us understand which visits become accounts. With
        your permission, we also use privacy-masked session replay to improve
        the product. No ads, no cross-site tracking, no selling your data.{" "}
        <a className="cc-link" href="https://condense.chat/legal/#doc=cookies">
          Cookie details
        </a>
        .
      </p>
      <div className="cc-actions">
        <button
          type="button"
          className="cc-btn cc-accept"
          onClick={() => save({ analytics: true, replay: true })}
        >
          Sure, go ahead
        </button>
        <button
          type="button"
          className="cc-btn cc-reject"
          onClick={() => save({ analytics: false, replay: false })}
        >
          Nope, just essentials
        </button>
        <button
          type="button"
          className="cc-btn cc-choose"
          onClick={() => setCustomizing(true)}
        >
          Let me choose
        </button>
      </div>
      {customizing && (
        <div className="cc-custom">
          <label className="cc-choice">
            <span className="cc-choice-copy">
              <strong>Analytics cookies</strong>
              <small>
                Page and device activity, source IP, and visit-to-account
                conversion.
              </small>
            </span>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(event) => {
                setAnalytics(event.target.checked);
                if (!event.target.checked) setReplay(false);
              }}
            />
          </label>
          <label className="cc-choice">
            <span className="cc-choice-copy">
              <strong>Session replay</strong>
              <small>
                Masked clicks and scrolling. Inputs, secrets, emails, and URL
                parameters are excluded.
              </small>
            </span>
            <input
              type="checkbox"
              checked={replay}
              disabled={!analytics}
              onChange={(event) => setReplay(event.target.checked)}
            />
          </label>
          <button
            type="button"
            className="cc-btn cc-save"
            onClick={() => save({ analytics, replay: analytics && replay })}
          >
            Save my choices
          </button>
        </div>
      )}
    </div>
  );
}

export { posthog };
